import { createContext } from 'lib/createContext'
import React, { PropsWithChildren, useState } from 'react'

type Workflow<Args extends Record<string, unknown> | undefined = undefined> = {
  open: Args extends undefined ? () => void : (args: Args) => void
  close: () => void
  isOpen: boolean
} & (Args extends undefined ? object : { data?: Args })

type RequirementFlowArgs = {
  requirement: {
    id: number
    name: string
    verificationType: string
    category: string
    mustHave: boolean
  }
  positionId: number
}

export type PositionDetailWorkflowsContextState = {
  editRequirementFlow: Workflow<RequirementFlowArgs>
  unassignRequirementFlow: Workflow<RequirementFlowArgs>
}

type WorkflowState<T extends Record<string, unknown> | undefined = undefined> =
  T extends undefined
    ? boolean
    : {
        isOpen: boolean
        data?: T
      }

const [PositionDetailWorkflowsContext, usePositionDetailWorkflowsContextValue] =
  createContext<PositionDetailWorkflowsContextState>({
    name: 'PositionDetailWorkflowsContext',
  })

export const PositionDetailWorkflowsProvider = ({
  children,
}: PropsWithChildren) => {
  const [editRequirementFlow, setEditRequirementFlow] = useState<
    WorkflowState<RequirementFlowArgs>
  >({ isOpen: false, data: undefined })

  const [unassignRequirementFlow, setUnassignRequirementFlow] = useState<
    WorkflowState<RequirementFlowArgs>
  >({ isOpen: false, data: undefined })

  const value: PositionDetailWorkflowsContextState = {
    editRequirementFlow: {
      open: (data) => setEditRequirementFlow({ isOpen: true, data }),
      close: () => setEditRequirementFlow({ isOpen: false, data: undefined }),
      isOpen: editRequirementFlow.isOpen,
      data: editRequirementFlow.data,
    },
    unassignRequirementFlow: {
      open: (data) => setUnassignRequirementFlow({ isOpen: true, data }),
      close: () =>
        setUnassignRequirementFlow({ isOpen: false, data: undefined }),
      isOpen: unassignRequirementFlow.isOpen,
      data: unassignRequirementFlow.data,
    },
  }

  return (
    <PositionDetailWorkflowsContext.Provider value={value}>
      {children}
    </PositionDetailWorkflowsContext.Provider>
  )
}

export { usePositionDetailWorkflowsContextValue }
